import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2Icon } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import Logo from "../../components/Logo";
import { useNotificationsProvider } from "../../context/NotificationsContext";
import translateCognitoError from "../../utils/translateCognitoError";

import {
  completeNewPassword,
  login,
  sendCognitoMFACode,
} from "./actions";
import { useVerify2FAMutation } from "../../services/api";
import {
  selectCognitoTotpRequired,
  selectNewPasswordChallenge,
  selectNewPasswordRequired,
  selectSession,
  selectTwoFactorData,
  selectTwoFactorRequired,
  selectUserError,
} from "./selectors";

interface UserAttributes {
  [key: string]: string | undefined;
}

interface NewPasswordChallengePayload {
  userAttributes?: UserAttributes;
}

interface TwoFactorPayload {
  accountId?: string;
}

interface UserError {
  error?: string;
}

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [verify2FA] = useVerify2FAMutation();
  const { showNotification } = useNotificationsProvider();

  const isAuth = useSelector(selectSession());
  const userError = useSelector(selectUserError()) as UserError | null;
  const requires2FA = useSelector(selectTwoFactorRequired());
  const twoFactorData = useSelector(
    selectTwoFactorData()
  ) as TwoFactorPayload | null;
  const requiresNewPassword = useSelector(selectNewPasswordRequired());
  const newPasswordChallenge = useSelector(
    selectNewPasswordChallenge()
  ) as NewPasswordChallengePayload | null;
  const requiresCognitoTotp = useSelector(selectCognitoTotpRequired());

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isAuth) {
      navigate("/dashboard/app", { replace: true });
      return;
    }
    if (
      userError &&
      !requires2FA &&
      !requiresNewPassword &&
      !requiresCognitoTotp
    ) {
      setSubmitting(false);
      showNotification(
        "error",
        translateCognitoError(userError?.error, t, "login.invalid-credentials")
      );
    } else if (userError && requires2FA) {
      setSubmitting(false);
      showNotification("error", t("login.invalid-2fa-token"));
    } else if (userError && requiresNewPassword) {
      setSubmitting(false);
      showNotification(
        "error",
        translateCognitoError(userError?.error, t, "login.invalid-new-password")
      );
    } else if (userError && requiresCognitoTotp) {
      setSubmitting(false);
      showNotification(
        "error",
        translateCognitoError(userError?.error, t, "login.invalid-2fa-token")
      );
    } else if (
      (requires2FA || requiresNewPassword || requiresCognitoTotp) &&
      !userError
    ) {
      // Transitioned to a challenge screen — stop the loading state on the
      // previous form so the new screen renders without a stale spinner.
      setSubmitting(false);
    }
  }, [
    isAuth,
    userError,
    requires2FA,
    requiresNewPassword,
    requiresCognitoTotp,
    navigate,
    showNotification,
    t,
  ]);

  if (requiresNewPassword) {
    return (
      <NewPasswordChallenge
        submitting={submitting}
        onSubmit={(password) => {
          setSubmitting(true);
          dispatch(
            completeNewPassword(
              password,
              newPasswordChallenge?.userAttributes || {}
            )
          );
        }}
      />
    );
  }

  if (requiresCognitoTotp) {
    return (
      <CognitoTotpChallenge
        submitting={submitting}
        onSubmit={(token) => {
          setSubmitting(true);
          dispatch(sendCognitoMFACode(token));
        }}
      />
    );
  }

  if (requires2FA) {
    return (
      <LegacyTwoFactorChallenge
        submitting={submitting}
        accountId={twoFactorData?.accountId}
        onSubmit={(token) => {
          if (!twoFactorData?.accountId) return;
          setSubmitting(true);
          verify2FA({ accountId: twoFactorData.accountId, token })
            .unwrap()
            .catch(() => undefined)
            .finally(() => setSubmitting(false));
        }}
      />
    );
  }

  return (
    <CredentialsForm
      submitting={submitting}
      onSubmit={(values) => {
        setSubmitting(true);
        dispatch(login(values.email, values.password));
      }}
    />
  );
}

// ---------- Shared layout ----------------------------------------------

interface PageShellProps {
  title: string;
  children: React.ReactNode;
}

function PageShell({ title, children }: PageShellProps) {
  return (
    <>
      <Helmet>
        <title>{title} | Betterfield</title>
      </Helmet>
      <main className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-6 py-12">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>
        {children}
      </main>
    </>
  );
}

function SubmitButton({
  submitting,
  disabled,
  children,
}: {
  submitting: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="submit"
      size="lg"
      className="w-full"
      disabled={submitting || disabled}
    >
      {submitting && <Loader2Icon className="size-4 animate-spin" />}
      {children}
    </Button>
  );
}

// ---------- Credentials (normal login) ---------------------------------

interface CredentialsValues {
  email: string;
  password: string;
}

interface CredentialsFormProps {
  submitting: boolean;
  onSubmit: (values: CredentialsValues) => void;
}

function CredentialsForm({ submitting, onSubmit }: CredentialsFormProps) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const schema = z.object({
    email: z
      .string()
      .min(1, t("login.required-email"))
      .email(t("login.invalid-email")),
    password: z.string().min(1, t("login.required-password")),
  });
  type Values = z.infer<typeof schema>;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  return (
    <PageShell title={t("login.title")}>
      <Form {...form}>
        <form
          autoComplete="off"
          noValidate
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <h1 className="text-2xl font-bold tracking-tight">
            {t("login.title")}
          </h1>

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("login.email-label")}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="username"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("login.password-label")}</FormLabel>
                <FormControl>
                  <PasswordInput
                    autoComplete="current-password"
                    show={showPassword}
                    onToggleShow={() => setShowPassword((s) => !s)}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-col items-center gap-3">
            <SubmitButton submitting={submitting}>
              {t("login.login-button")}
            </SubmitButton>
            <Button asChild variant="link" size="sm">
              <RouterLink to="/forgot-password">
                {t("login.forgot-password")}
              </RouterLink>
            </Button>
          </div>
        </form>
      </Form>
    </PageShell>
  );
}

// ---------- Cognito FORCE_CHANGE_PASSWORD challenge --------------------

interface NewPasswordChallengeProps {
  submitting: boolean;
  onSubmit: (password: string) => void;
}

function NewPasswordChallenge({
  submitting,
  onSubmit,
}: NewPasswordChallengeProps) {
  const { t } = useTranslation();
  const { showNotification } = useNotificationsProvider();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      showNotification(
        "error",
        t("login.password-mismatch", "Passwords do not match")
      );
      return;
    }
    // Length / complexity rules live in the Cognito User Pool policy. Let
    // the SDK surface those failures rather than duplicating them here.
    onSubmit(password);
  }

  return (
    <PageShell
      title={t("login.set-new-password-title", "Set a new password")}
    >
      <form
        autoComplete="off"
        noValidate
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        <div className="space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">
            {t("login.set-new-password-title", "Set a new password")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "login.set-new-password-description",
              "Your account requires a new password before you can sign in."
            )}
          </p>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="login-new-password"
            className="text-sm font-medium"
          >
            {t("login.new-password-label", "New password")}
          </label>
          <PasswordInput
            id="login-new-password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            show={showPassword}
            onToggleShow={() => setShowPassword((s) => !s)}
          />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="login-confirm-new-password"
            className="text-sm font-medium"
          >
            {t("login.confirm-new-password-label", "Confirm new password")}
          </label>
          <PasswordInput
            id="login-confirm-new-password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            show={showPassword}
            onToggleShow={() => setShowPassword((s) => !s)}
          />
        </div>

        <div className="flex flex-col items-center gap-3">
          <SubmitButton
            submitting={submitting}
            disabled={!password || password !== confirm}
          >
            {t("login.set-password-button", "Set password and continue")}
          </SubmitButton>
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={() => {
              // Hard navigate to /login (single round-trip). The asChild +
              // RouterLink + reload combo otherwise did an SPA transition
              // first and then a full reload, causing a visible flash.
              window.location.href = "/login";
            }}
          >
            {t("login.back-to-login", "Back to Login")}
          </Button>
        </div>
      </form>
    </PageShell>
  );
}

// ---------- Cognito Software TOTP MFA challenge ------------------------

interface CognitoTotpChallengeProps {
  submitting: boolean;
  onSubmit: (token: string) => void;
}

function CognitoTotpChallenge({
  submitting,
  onSubmit,
}: CognitoTotpChallengeProps) {
  const { t } = useTranslation();
  const [token, setToken] = useState("");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (token.length === 6) onSubmit(token);
  }

  return (
    <PageShell title={t("login.2fa-title", "Two-Factor Authentication")}>
      <form
        autoComplete="off"
        noValidate
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        <div className="space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">
            {t("login.2fa-title", "Two-Factor Authentication")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "login.2fa-description-cognito",
              "Enter the 6-digit code from your authenticator app."
            )}
          </p>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="login-totp-code"
            className="text-sm font-medium"
          >
            {t("login.2fa-code-label", "Authentication Code")}
          </label>
          <Input
            id="login-totp-code"
            type="text"
            autoComplete="one-time-code"
            inputMode="numeric"
            maxLength={6}
            autoFocus
            value={token}
            onChange={(e) =>
              setToken(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            className="text-center text-2xl tracking-[0.5rem]"
          />
        </div>

        <div className="flex flex-col items-center gap-3">
          <SubmitButton submitting={submitting} disabled={token.length !== 6}>
            {t("login.verify-button", "Verify")}
          </SubmitButton>
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={() => {
              // Hard navigate to /login (single round-trip). The asChild +
              // RouterLink + reload combo otherwise did an SPA transition
              // first and then a full reload, causing a visible flash.
              window.location.href = "/login";
            }}
          >
            {t("login.back-to-login", "Back to Login")}
          </Button>
        </div>
      </form>
    </PageShell>
  );
}

// ---------- Legacy 2FA challenge (6-digit code or 8-char recovery) -----

interface LegacyTwoFactorChallengeProps {
  submitting: boolean;
  accountId: string | undefined;
  onSubmit: (token: string) => void;
}

function LegacyTwoFactorChallenge({
  submitting,
  accountId,
  onSubmit,
}: LegacyTwoFactorChallengeProps) {
  const { t } = useTranslation();
  const [token, setToken] = useState("");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (token.length >= 6 && accountId) onSubmit(token);
  }

  return (
    <PageShell title={t("login.2fa-title", "Two-Factor Authentication")}>
      <form
        autoComplete="off"
        noValidate
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        <div className="space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">
            {t("login.2fa-title", "Two-Factor Authentication")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "login.2fa-description",
              "Enter the 6-digit code from your authenticator app or an 8-character recovery code."
            )}
          </p>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="login-2fa-code"
            className="text-sm font-medium"
          >
            {t("login.2fa-code-label", "Authentication Code")}
          </label>
          <Input
            id="login-2fa-code"
            type="text"
            autoComplete="one-time-code"
            maxLength={8}
            autoFocus
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className="text-center text-2xl tracking-[0.5rem]"
          />
        </div>

        <div className="flex flex-col items-center gap-3">
          <SubmitButton submitting={submitting} disabled={token.length < 6}>
            {t("login.verify-button", "Verify")}
          </SubmitButton>
          <Button asChild variant="link" size="sm">
            <RouterLink to="/reset-2fa">
              {t("login.lost-2fa-access", "Lost access to authenticator?")}
            </RouterLink>
          </Button>
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={() => {
              // Hard navigate to /login (single round-trip). The asChild +
              // RouterLink + reload combo otherwise did an SPA transition
              // first and then a full reload, causing a visible flash.
              window.location.href = "/login";
            }}
          >
            {t("login.back-to-login", "Back to Login")}
          </Button>
        </div>
      </form>
    </PageShell>
  );
}
